/* global wlgpi_params */

// import { ColorSetting } from '@10up/block-components';
import ApiFrmCustom from '../../../../../plugins/webikon-loopia-generic-platform-integration/assets/public/js/wlgpi-custom';
import Slider from '@scripts/components/slider';
import { decodeEntities } from '../misc/utils';
import { OperationSystems } from '@scripts/block-components/products/operation-systems';
import Alpine from 'alpinejs';

const forms = document.querySelectorAll('.js-configurator-form');

const getSliderValue = (slider, context = 'cart') => {
  if (!slider) {
    return { value: 0, min: 0, max: 0 };
  }

  let value = slider.getValue();
  let min = slider.sliderMin;
  let max = slider.sliderMax;
  let step = slider.sliderStep;

  // Ensure we have valid numbers
  value = isNaN(value) || value === undefined || value === null ? 0 : value;
  min = isNaN(min) || min === undefined || min === null ? 0 : min;
  max = isNaN(max) || max === undefined || max === null ? 0 : max;
  step = isNaN(step) || step === undefined || step === null ? 1 : step;

  // Convert back to original API values if we have original parameters stored
  // But skip offset subtraction for Windows VPS from BIZ API
  if (slider.originalParams && !slider.noOffsetSubtraction) {
    // Calculate the offset that was applied for display
    const defaultOffset =
      slider.originalParams.default !== undefined ? slider.originalParams.default - slider.originalParams.min : 0;

    // Convert display values back to original API values
    value = value - defaultOffset;
    min = slider.originalParams.min;
    max = slider.originalParams.max;
  }

  // Convert value, min, and max from GB to MB if the unitSingular is 'gb'
  // For pricing API: ALWAYS convert to MB (even for Windows VPS)
  // For cart API: Skip conversion for Windows VPS (it expects GB values directly)
  const shouldConvertToMB = slider.unitSingular && slider.unitSingular.toLowerCase() === 'gb';
  const isWindowsVPS = slider.noOffsetSubtraction;

  if (shouldConvertToMB && (context === 'pricing' || !isWindowsVPS)) {
    value *= 1024;
    min *= 1024;
    max *= 1024;
  }

  const result = {
    // Convert to number
    value: +value,
    min: +min,
    max: +max,
    step: +step,
  };

  return result;
};

// Find the closest parent element that has an Alpine wrapper ref
const findAlpineWrapper = (element) => {
  let current = element;
  while (current) {
    if (current.hasAttribute('x-ref') && current.getAttribute('x-ref') === 'wrapper') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const getAddToCartAlpineJsonObject = (addToCartEl, params) => {
  const originalAddToCartAlpineString = addToCartEl.getAttribute('@click');

  const originalAddToCartAlpineJsonString = originalAddToCartAlpineString.match(/{.*}/)[0];

  let originalAddToCartAlpineJsonObject = {};
  try {
    originalAddToCartAlpineJsonObject = JSON.parse(originalAddToCartAlpineJsonString);
  } catch (error) {
    return originalAddToCartAlpineString;
  }

  // Check if this is Windows VPS by looking at params or original object
  const isWindowsVps =
    (params.osName && params.osName.toLowerCase().includes('windows')) ||
    (originalAddToCartAlpineJsonObject.osName &&
      originalAddToCartAlpineJsonObject.osName.toLowerCase().includes('windows'));

  // Filter out conflicting parameters
  if (isWindowsVps && params.disk_size !== undefined) {
    // For Windows VPS with disk_size, remove all capacity parameters
    delete originalAddToCartAlpineJsonObject.capacity;
    delete originalAddToCartAlpineJsonObject.capacity_min;
    delete originalAddToCartAlpineJsonObject.capacity_max;
    delete originalAddToCartAlpineJsonObject.capacity_step;

    // Also remove from params to be safe
    delete params.capacity;
    delete params.capacity_min;
    delete params.capacity_max;
    delete params.capacity_step;
  }

  if (!isWindowsVps && params.capacity !== undefined) {
    // For regular VPS with capacity, remove all disk_size parameters
    delete originalAddToCartAlpineJsonObject.disk_size;
    delete originalAddToCartAlpineJsonObject.disk_size_min;
    delete originalAddToCartAlpineJsonObject.disk_size_max;
    delete originalAddToCartAlpineJsonObject.disk_size_step;

    // Also remove from params to be safe
    delete params.disk_size;
    delete params.disk_size_min;
    delete params.disk_size_max;
    delete params.disk_size_step;
  }

  // Append the params object to the originalAddToCartAlpineJsonObject
  Object.assign(originalAddToCartAlpineJsonObject, params);

  // Convert the updated originalAddToCartAlpineJsonObject back to a string
  const updatedJsonString = JSON.stringify(originalAddToCartAlpineJsonObject);

  // Replace the JSON string in the original string with the updated JSON string
  return originalAddToCartAlpineString.replace(/{.*}/, updatedJsonString);
};

if (forms && forms.length) {
  forms.forEach((form) => {
    let slidersEl = form.querySelectorAll('.js-range-slider');
    let sliderInstances = {};
    let params = {};
    let updatePrices = true; // Flag to control price updates

    const wrapper = findAlpineWrapper(form);

    // Listen for package-addons-updated event at document level
    document.addEventListener('package-addons-updated', (event) => {
      // Check if this event is for our form by checking if the event target is within our form's wrapper
      const eventWrapper = findAlpineWrapper(event.target);
      if (!wrapper || !eventWrapper || wrapper !== eventWrapper) {
        return;
      }

      if (!updatePrices) {
        return;
      }

      // Update form data with addon parameters
      const formData = form.dataset.data ? JSON.parse(form.dataset.data) : {};
      Object.assign(formData, event.detail.params);
      form.dataset.data = JSON.stringify(formData);

      // Get the store from the wrapper
      const store = wrapper.__x?.data?.store;
      if (store) {
        store.selectedAddons = event.detail.addons || [];
      }

      // Update button parameters and prices
      updateAddToCartParams(form, sliderInstances, wrapper);
      getPrices(form, sliderInstances, params, wrapper);
    });

    if (slidersEl && slidersEl.length) {
      slidersEl.forEach((sliderEl) => {
        const slider = new Slider(sliderEl);

        slider.onChange(function () {
          if (updatePrices) {
            // Check if we should update prices
            getPrices(form, sliderInstances, params, wrapper);
          }
        });

        sliderInstances[slider.type] = slider;
      });
    }

    let osSelectEl = form.querySelector('.js-configurator-form__os-select select');

    if (osSelectEl) {
      osSelectEl.addEventListener('change', async () => {
        let osNameTemplate = getOsNameTemplate(osSelectEl.value);

        params['osName'] = osNameTemplate.osName;
        params['osTemplate'] = osNameTemplate.osTemplate;

        if (updatePrices) {
          // Merge form data (which includes uuid) with params for updateSlidersMinMax
          const formData = form.dataset.data ? JSON.parse(form.dataset.data) : {};
          const mergedParams = Object.assign({}, formData, params);

          // Wait for sliders to be updated before fetching prices
          await updateSlidersMinMax(sliderInstances, mergedParams);

          // Check if we should update prices
          getPrices(form, sliderInstances, params, wrapper);
        }
      });

      // Store initial OS info in params for later use
      let initialOsNameTemplate = getOsNameTemplate(osSelectEl.value);
      params['osName'] = initialOsNameTemplate.osName;
      params['osTemplate'] = initialOsNameTemplate.osTemplate;
    }

    // Get website builder packages parameter
    const websiteBuilderPackagesSelect = form.querySelector('.js-website-builder-package-select select');
    if (websiteBuilderPackagesSelect) {
      websiteBuilderPackagesSelect.addEventListener('change', () => {
        params['addon_ecommerce_subscription'] = websiteBuilderPackagesSelect.value;

        // Update formset
        const formDataSet = JSON.parse(form.dataset.data);
        formDataSet.addon_ecommerce_subscription = websiteBuilderPackagesSelect.value;
        form.dataset.data = JSON.stringify(formDataSet);

        if (updatePrices) {
          // Check if we should update prices
          getPrices(form, sliderInstances, params, wrapper);
        }
      });

      params['addon_ecommerce_subscription'] = websiteBuilderPackagesSelect.value;
    }

    // Get cookie compliance level parameter
    const cookieComplianceLevelSelect = form.querySelector('.js-cookie-compliance-level-select select');
    if (cookieComplianceLevelSelect) {
      cookieComplianceLevelSelect.addEventListener('change', () => {
        params['level'] = cookieComplianceLevelSelect.value;

        // Update formset
        const formDataSet = JSON.parse(form.dataset.data);
        formDataSet.level = cookieComplianceLevelSelect.value;
        form.dataset.data = JSON.stringify(formDataSet);

        if (updatePrices) {
          // Check if we should update prices
          getPrices(form, sliderInstances, params, wrapper);
        }
      });
    }

    // Initial call to update prices after all sliders and selectboxes are initialized
    if (updatePrices) {
      getPrices(form, sliderInstances, params, wrapper);
    }

    // After initial setup, set flag to true to allow updates
    updatePrices = true;
  });
}

async function getPrices(form, sliderInstances, additionalParams = {}, wrapper = null) {
  const Api = new ApiFrmCustom(form.dataset.homeurl, wlgpi_params.rest_nonce);
  const BizApi = new ApiFrmCustom(form.dataset.homeurl, wlgpi_params.rest_nonce, 'biz');

  const formAction = form.getAttribute('action');

  form.closest('.product-card')?.classList.add('is-loading');
  form.closest('.server-configurator-card')?.classList.add('is-loading');

  let prices = [];

  if (formAction === 'hosting-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getHostingPrices(sliderInstances, BizApi, additionalParams);
  }

  if (formAction === 'website-builder-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getWebsiteBuilderPrices(sliderInstances, BizApi, additionalParams);
  }

  if (formAction === 'webnode-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getWebnodePrices(sliderInstances, BizApi, additionalParams);
  }

  if (formAction === 'vps-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    // Handle Windows VPS parameter filtering
    const osName = additionalParams.osName || '';
    const isWindowsVps = osName && osName.toLowerCase().includes('windows');

    if (isWindowsVps) {
      applyWindowsVpsParamShape(additionalParams, sliderInstances);
    } else {
      // For regular VPS, remove disk_size parameters
      delete additionalParams.disk_size;
      delete additionalParams.disk_size_min;
      delete additionalParams.disk_size_max;
      delete additionalParams.disk_size_step;
    }

    prices = await getVpsPrices(sliderInstances, Api, additionalParams);
  }

  if (formAction === 'private-server-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    // Windows OS on a private-server card → use the windows-vps pricing
    // endpoint + admin-configured Windows packageId, since BIZ resolves the
    // package by uuid not osName.
    const osName = additionalParams.osName || '';
    const isWindowsVps = osName && osName.toLowerCase().includes('windows');

    if (isWindowsVps) {
      if (additionalParams.windows_uuid) {
        additionalParams.uuid = additionalParams.windows_uuid;
      }
      applyWindowsVpsParamShape(additionalParams, sliderInstances);
      prices = await getVpsPrices(sliderInstances, Api, additionalParams);
    } else {
      prices = await getPrivateServerPrices(sliderInstances, Api, additionalParams);
    }
  }

  if (formAction === 'storage-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getStoragePrices(sliderInstances, Api, additionalParams);
  }

  if (formAction === 'vdc-prices') {
    // TODO implement ak treba
  }

  if (formAction === 'business-mail-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getBusinessMailPrices(sliderInstances, BizApi, additionalParams);
  }

  if (formAction === 'online-marketing-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getOnlineMarketingPrices(BizApi, additionalParams);
  }

  if (formAction === 'cookie-compliance-prices') {
    // Get data from form but check if data exists
    const data = form.dataset.data ? JSON.parse(form.dataset.data) : {};

    // Merge data with additionalParams
    additionalParams = Object.assign(data, additionalParams);

    prices = await getCookieCompliancePrices(BizApi, additionalParams);
  }

  const priceElSelector = form.dataset.priceSelector;
  let formTotalPriceEl = form.closest(priceElSelector);

  // If price total was not found, loop through parents to find the price element. Add there some limit of 10 parents max
  if (!formTotalPriceEl) {
    let parent = form.parentElement;
    let i = 0;
    while (!formTotalPriceEl && i < 10) {
      formTotalPriceEl = parent.querySelector(priceElSelector);
      parent = parent.parentElement;
      i++;
    }
  }

  // Check if all form sliders are at the start position
  let areAllSlidersAtStartPositionArr = [];
  Object.keys(sliderInstances).forEach((key) => {
    areAllSlidersAtStartPositionArr.push(
      getSliderValue(sliderInstances[key])?.value === getSliderValue(sliderInstances[key])?.min,
    );
  });

  let areAllSlidersAtStartPosition = areAllSlidersAtStartPositionArr.every((value) => value === true);

  // Skip update when BIZ returns `{error: ...}` so we don't paint "undefined".
  const hasValidPrices = prices && !prices.error && prices.total !== undefined && prices.totalVat !== undefined;

  if (formTotalPriceEl && hasValidPrices) {
    // Update alpine element price data
    formTotalPriceEl.dispatchEvent(
      new CustomEvent('update-prices', {
        detail: {
          price: decodeEntities(prices.total),
          priceVat: decodeEntities(prices.totalVat),
          priceProlong: prices.priceProlong ? decodeEntities(prices.priceProlong) : null,
          priceProlongVat: prices.priceProlongVat ? decodeEntities(prices.priceProlongVat) : null,
          // If all sliders are at the start position, set isInitialPrice to true
          isInitialPrice: areAllSlidersAtStartPosition ? true : false,
        },
      }),
    );
  }

  sliderInstances['cpu']?.setPrice(prices?.partial?.cpuVat, prices?.partial?.cpu);
  sliderInstances['ram']?.setPrice(prices?.partial?.ramVat, prices?.partial?.ram);
  sliderInstances['capacity']?.setPrice(prices?.partial?.capacityVat, prices?.partial?.capacity);
  sliderInstances['domains_count']?.setPrice(prices?.partial?.domainsCountVat, prices?.partial?.domainsCount);
  sliderInstances['addon_email_storage']?.setPrice(
    prices?.partial?.addonEmailStorageVat,
    prices?.partial?.addonEmailStorage,
  );
  sliderInstances['licences']?.setPrice(prices?.partial?.licencesVat, prices?.partial?.licences);

  updateAddToCartParams(form, sliderInstances, wrapper);

  form.closest('.product-card')?.classList?.remove('is-loading');
  form.closest('.server-configurator-card')?.classList.remove('is-loading');

  // Update period text after prices are updated (for Windows VPS, incl. the
  // private-server card falling back to the windows-vps endpoint).
  if (formAction === 'vps-prices' || formAction === 'private-server-prices') {
    const osName = additionalParams.osName || '';
    const isWindowsVps = osName && osName.toLowerCase().includes('windows');
    updatePeriodText(form, isWindowsVps);
  }
}
// Reshape additionalParams for the windows-vps pricing path: drop capacity-*,
// promote the capacity slider into disk_size-*, and drop OS/windows-licence
// hints that the legacy endpoint ignores.
function applyWindowsVpsParamShape(additionalParams, sliderInstances) {
  delete additionalParams.capacity;
  delete additionalParams.capacity_min;
  delete additionalParams.capacity_max;
  delete additionalParams.capacity_step;

  if (sliderInstances.capacity && !additionalParams.disk_size) {
    const capacityInfo = getSliderValue(sliderInstances.capacity);
    additionalParams.disk_size = capacityInfo.value;
    additionalParams.disk_size_min = capacityInfo.min;
    additionalParams.disk_size_max = capacityInfo.max;
    additionalParams.disk_size_step = capacityInfo.step;
  }

  delete additionalParams.osTemplate;
  delete additionalParams.windowsLicenceText;
  delete additionalParams.windowsLicencePrice;
}

// Helper function to extract the slider value or use a fallback from additionalParams
function getValueOrDefault(additionalParams, slider, paramKey, conversionFunc = (val) => val) {
  if (slider) {
    // Use 'pricing' context to ensure values are in MB for pricing API
    const sliderInfo = getSliderValue(slider, 'pricing');
    return sliderInfo.value;
  } else {
    return conversionFunc(additionalParams[paramKey]);
  }
}

async function getVpsPrices(sliderInstances, Api, additionalParams = {}) {
  // Extracting values using the helper function
  const cpu = getValueOrDefault(additionalParams, sliderInstances.cpu, 'cpu');
  const ram = getValueOrDefault(additionalParams, sliderInstances.ram, 'ram');

  // For pricing API, always use capacity (in MB) for both Windows and Linux VPS
  // The backend handles conversion for BIZ API if needed
  const capacity = getValueOrDefault(additionalParams, sliderInstances.capacity, 'capacity');

  // Destructure remaining additionalParams directly
  const { period, osTemplate, uuid, osName } = additionalParams;

  // Pricing API always uses capacity parameter for both VPS types
  const prices = await Api.vpsPrices(period, cpu, ram, capacity, osName, osTemplate, undefined, undefined, uuid);

  return prices;
}

async function getPrivateServerPrices(sliderInstances, Api, additionalParams = {}) {
  // Sliders feed cpu (int), ram (MB) and capacity (MB) — matches BIZ private-server addon shape.
  const cpu = getValueOrDefault(additionalParams, sliderInstances.cpu, 'cpu');
  const ram = getValueOrDefault(additionalParams, sliderInstances.ram, 'ram');
  const capacity = getValueOrDefault(additionalParams, sliderInstances.capacity, 'capacity');

  const { period, uuid, osTemplate = '' } = additionalParams;

  const prices = await Api.privateServerPrices(uuid, {
    period,
    cpu,
    ram,
    capacity,
    osTemplate,
  });

  return prices;
}

async function getStoragePrices(sliderInstances, Api, additionalParams = {}) {
  // Extracting values using the helper function
  const capacity = getValueOrDefault(additionalParams, sliderInstances.capacity, 'capacity');

  // Destructure remaining additionalParams directly
  const { period } = additionalParams;

  // Fetch prices with the computed values
  const prices = await Api.storagePrices(period, capacity);

  return prices;
}

async function getBusinessMailPrices(sliderInstances, Api, additionalParams = {}) {
  // Extracting values using the helper function
  const licences = getValueOrDefault(additionalParams, sliderInstances.licences, 'licences');

  // Destructure remaining additionalParams directly
  const { period, uuid, licenceType } = additionalParams;

  // Fetch prices with the computed values
  const prices = await Api.businessMailPrices(uuid, period, licences, licenceType);

  return prices;
}

async function getOnlineMarketingPrices(Api, additionalParams = {}) {
  const { uuid, period } = additionalParams;

  // Create params object with period
  const params = { period };

  // Add addon parameters directly as key:value pairs
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (key.startsWith('addon_') && value === true) {
      params[key] = value;
    }
  });

  const prices = await Api.onlineMarketingPrices(uuid, params);

  return prices;
}

async function getHostingPrices(sliderInstances, Api, additionalParams = {}) {
  const { capacity, domains_count } = sliderInstances;
  const { uuid, period } = additionalParams;

  const capacityInfo = getSliderValue(capacity);
  const domainsCountInfo = getSliderValue(domains_count);

  const prices = await Api.hostingPrices(uuid, {
    period: period,
    domainsCount: domainsCountInfo.value,
    domainsCountMin: domainsCountInfo.min,
    domainsCountMax: domainsCountInfo.max,
    capacity: capacityInfo.value,
    capacityMin: capacityInfo.min,
    capacityMax: capacityInfo.max,
  });

  return prices;
}

async function getWebsiteBuilderPrices(sliderInstances, Api, additionalParams = {}) {
  const { addon_email_storage } = sliderInstances;
  const { uuid, period, addon_ecommerce_subscription } = additionalParams;

  const addonEmailStorageInfo = getSliderValue(addon_email_storage);

  const params = {
    period: period,
    addon_email_storage: addonEmailStorageInfo.value,
    addon_email_storage_min: addonEmailStorageInfo.min,
    addon_email_storage_max: addonEmailStorageInfo.max,
    addon_ecommerce_subscription: addon_ecommerce_subscription,
  };

  // Forward boolean `addon_*` flags from package-addons checkboxes.
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (key.startsWith('addon_') && value === true) {
      params[key] = value;
    }
  });

  const prices = await Api.websiteBuilderPrices(uuid, params);

  return prices;
}

async function getWebnodePrices(sliderInstances, Api, additionalParams = {}) {
  const { addon_email_storage } = sliderInstances;
  const { uuid, period } = additionalParams;

  const addonEmailStorageInfo = getSliderValue(addon_email_storage);

  const params = {
    period: period,
    addon_email_storage: addonEmailStorageInfo.value,
    addon_email_storage_min: addonEmailStorageInfo.min,
    addon_email_storage_max: addonEmailStorageInfo.max,
  };

  // Forward boolean `addon_*` flags from package-addons checkboxes.
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (key.startsWith('addon_') && value === true) {
      params[key] = value;
    }
  });

  const prices = await Api.webnodePrices(uuid, params);

  return prices;
}

async function getCookieCompliancePrices(Api, additionalParams = {}) {
  const { uuid, period, level } = additionalParams;

  const prices = await Api.cookieCompliancePrices(uuid, {
    period: period,
    level: level,
  });

  return prices;
}

function updateAddToCartParams(form, sliderInstances, wrapper) {
  const addToCartElSelector = form.dataset.buttonSelector;
  let addToCartEl = form.querySelector(addToCartElSelector);

  // If button was not found, loop through parents
  if (!addToCartEl) {
    let parent = form.parentElement;
    let i = 0;
    while (!addToCartEl && i < 10) {
      addToCartEl = parent.querySelector(addToCartElSelector);
      parent = parent.parentElement;
      i++;
    }
  }

  let params = {};

  // Check if this is a Windows VPS first
  let isWindowsVps = false;
  const osSelectEl = form.querySelector('.js-configurator-form__os-select select');
  if (osSelectEl) {
    let osNameTemplate = getOsNameTemplate(osSelectEl.value);
    isWindowsVps = osNameTemplate.osName && osNameTemplate.osName.toLowerCase().includes('windows');
    params['osName'] = osNameTemplate.osName;
    params['osTemplate'] = osNameTemplate.osTemplate;
  }

  // Add slider params
  Object.keys(sliderInstances).forEach((key) => {
    const valueInfo = getSliderValue(sliderInstances[key]);

    // For Windows VPS, convert capacity to disk_size and skip capacity parameters entirely
    if (isWindowsVps && key === 'capacity') {
      params['disk_size'] = valueInfo.value;
      params['disk_size_min'] = valueInfo.min;
      params['disk_size_max'] = valueInfo.max;
      params['disk_size_step'] = valueInfo.step;
      return; // Skip adding capacity parameters
    }

    // For regular VPS, don't add disk_size parameters
    if (!isWindowsVps && key === 'disk_size') {
      return; // Skip disk_size for regular VPS
    }

    params[`${key}_min`] = valueInfo.min;
    params[`${key}_max`] = valueInfo.max;
    params[`${key}_step`] = valueInfo.step;
    params[key] = valueInfo.value;
  });

  // Get addons from wrapper's store using Alpine.$data
  if (wrapper) {
    try {
      const store = Alpine?.$data(wrapper)?.store;
      if (store?.selectedAddons) {
        store.selectedAddons.forEach((addon) => {
          params[addon.param] = true;
        });
      }
    } catch (error) {
      // Silently ignore Alpine store access errors
    }
  }

  // Add website builder params
  const websiteBuilderPackagesSelect = form.querySelector('.js-website-builder-package-select select');
  if (websiteBuilderPackagesSelect) {
    params['addon_ecommerce_subscription'] = websiteBuilderPackagesSelect.value;
  }

  // Add cookie compliance params
  const cookieComplianceLevelSelect = form.querySelector('.js-cookie-compliance-level-select select');
  if (cookieComplianceLevelSelect) {
    params['level'] = cookieComplianceLevelSelect.value;
  }

  // Windows OS on a private-server card uses the admin-configured Windows
  // packageId (uuid) instead of the default Linux one.
  if (isWindowsVps && form.dataset.product === 'private-server') {
    const formData = form.dataset.data ? JSON.parse(form.dataset.data) : {};
    if (formData.windows_uuid) {
      params['uuid'] = formData.windows_uuid;
    }
  }

  // Get the current Alpine.js @click attribute
  const originalAddToCartAlpineString = addToCartEl.getAttribute('@click');

  // Update the product code based on OS selection. Both vps and private-server
  // cards fall back to windows-vps when a Windows OS is picked; on switch back
  // to Linux we restore whichever product the card was rendered with.
  let updatedAddToCartString = originalAddToCartAlpineString;
  if (originalAddToCartAlpineString) {
    if (isWindowsVps) {
      if (originalAddToCartAlpineString.includes("'vps'")) {
        updatedAddToCartString = originalAddToCartAlpineString.replace("'vps'", "'windows-vps'");
      } else if (originalAddToCartAlpineString.includes("'private-server'")) {
        updatedAddToCartString = originalAddToCartAlpineString.replace("'private-server'", "'windows-vps'");
      }
    } else if (originalAddToCartAlpineString.includes("'windows-vps'")) {
      const restoreProduct = form.dataset.product || 'vps';
      updatedAddToCartString = originalAddToCartAlpineString.replace("'windows-vps'", `'${restoreProduct}'`);
    }
  }

  // Update the parameters in the Alpine.js call
  const addToCartAlpineJson = getAddToCartAlpineJsonObject(addToCartEl, params);

  if (addToCartEl.attributes['@click']) {
    // If we updated the product code, use the updated string as base, otherwise use the params-updated version
    if (updatedAddToCartString !== originalAddToCartAlpineString) {
      // Parse and update the parameters in the modified string
      try {
        const jsonMatch = updatedAddToCartString.match(/{.*}/);
        if (jsonMatch) {
          const originalJson = JSON.parse(jsonMatch[0]);
          const updatedJson = Object.assign(originalJson, params);
          const updatedJsonString = JSON.stringify(updatedJson);
          addToCartEl.attributes['@click'].nodeValue = updatedAddToCartString.replace(/{.*}/, updatedJsonString);
        } else {
          addToCartEl.attributes['@click'].nodeValue = updatedAddToCartString;
        }
      } catch (error) {
        console.error('Error updating Windows VPS add to cart params:', error);
        addToCartEl.attributes['@click'].nodeValue = addToCartAlpineJson;
      }
    } else {
      addToCartEl.attributes['@click'].nodeValue = addToCartAlpineJson;
    }
  }
}

function getOsNameTemplate(osValue) {
  if (!osValue) {
    return {
      osName: '',
      osTemplate: 'minimal',
    };
  }

  let selectedOs = osValue.split('|');

  return {
    osName: selectedOs[0] !== undefined ? selectedOs[0] : '',
    osTemplate: selectedOs[1] !== undefined ? selectedOs[1] : 'minimal',
  };
}

function updatePeriodText(form, isWindowsVps) {
  const periodTextElements = form.querySelectorAll('.js-period-text');
  periodTextElements.forEach((element) => {
    const period = parseInt(element.dataset.period);
    const monthText = element.dataset.monthText || '/month';
    const yearText = element.dataset.yearText || '/year';

    if (isWindowsVps) {
      // For Windows VPS, always show /month
      element.textContent = monthText;
    } else {
      // For regular VPS, show based on period
      element.textContent = period === 1 ? monthText : yearText;
    }
  });
}

/**
 * Fetch Windows VPS parameters from FRM Product Catalog API via REST API
 */
async function fetchWindowsVpsParameters(osName) {
  try {
    const url = new URL('/wp-json/wlgpi/biz/custom/vps-parameters', window.location.origin);
    url.searchParams.append('osName', osName);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[fetchWindowsVpsParameters] API Error:', error.message || 'Unknown error');
      return null;
    }

    const result = await response.json();

    if (result && result.sliders) {
      return result.sliders;
    } else {
      console.error('[fetchWindowsVpsParameters] Invalid response format');
      return null;
    }
  } catch (error) {
    console.error('[fetchWindowsVpsParameters] Fetch error:', error);
    return null;
  }
}

async function updateSlidersMinMax(sliderInstances, params = {}) {
  // Find operation system by oSName
  const osName = params.osName;
  const os = OperationSystems.find((os) => os.title === osName);

  if (!os) {
    return;
  }

  // Check if this is Windows VPS
  const isWindowsVps = osName && osName.toLowerCase().includes('windows');

  // For Windows VPS, try to fetch parameters from FRM Product Catalog API
  if (isWindowsVps) {
    const apiParams = await fetchWindowsVpsParameters(osName);

    if (apiParams) {
      // Update sliders with API parameters
      if (sliderInstances['cpu'] && apiParams.cpu) {
        sliderInstances['cpu'].originalParams = {
          min: apiParams.cpu.min,
          max: apiParams.cpu.max,
          step: apiParams.cpu.step,
          default: apiParams.cpu.default,
        };
        sliderInstances['cpu'].noOffsetSubtraction = true;
        sliderInstances['cpu'].setMinMax(apiParams.cpu.min, apiParams.cpu.max);
        sliderInstances['cpu'].setStep(apiParams.cpu.step);
        sliderInstances['cpu'].setDefaultDisplay(apiParams.cpu.default);
      }

      if (sliderInstances['ram'] && apiParams.ram) {
        // API returns MB, convert to GB for display
        const ramMinGB = apiParams.ram.min / 1024;
        const ramMaxGB = apiParams.ram.max / 1024;
        const ramStepGB = apiParams.ram.step / 1024;
        const ramDefaultGB = apiParams.ram.default / 1024;

        sliderInstances['ram'].originalParams = {
          min: ramMinGB,
          max: ramMaxGB,
          step: ramStepGB,
          default: ramDefaultGB,
        };
        sliderInstances['ram'].noOffsetSubtraction = true;
        sliderInstances['ram'].setMinMax(ramMinGB, ramMaxGB);
        sliderInstances['ram'].setStep(ramStepGB);
        sliderInstances['ram'].setDefaultDisplay(ramDefaultGB);
      }

      if (sliderInstances['capacity'] && apiParams.capacity) {
        // API returns MB, convert to GB for display
        const capMinGB = apiParams.capacity.min / 1024;
        const capMaxGB = apiParams.capacity.max / 1024;
        const capStepGB = apiParams.capacity.step / 1024;
        const capDefaultGB = apiParams.capacity.default / 1024;

        sliderInstances['capacity'].originalParams = {
          min: capMinGB,
          max: capMaxGB,
          step: capStepGB,
          default: capDefaultGB,
        };
        sliderInstances['capacity'].noOffsetSubtraction = true;
        sliderInstances['capacity'].setMinMax(capMinGB, capMaxGB);
        sliderInstances['capacity'].setStep(capStepGB);
        sliderInstances['capacity'].setDefaultDisplay(capDefaultGB);
      }

      // Return early since we've updated from API
      return;
    } else {
      console.warn('[updateSlidersMinMax] Failed to fetch API parameters, falling back to os.json');
    }
  }

  // Set min and max by OperationSystems.parameters
  if (sliderInstances['cpu']) {
    // Store original parameters for API conversion
    sliderInstances['cpu'].originalParams = {
      min: os.parameters.cpu_min,
      max: os.parameters.cpu_max,
      step: os.parameters.cpu_step,
      default: os.parameters.cpu_default,
    };

    // For Windows VPS, disable offset subtraction
    sliderInstances['cpu'].noOffsetSubtraction = isWindowsVps;

    // If we have a default value, adjust the display range
    let displayMin = os.parameters.cpu_min;
    let displayMax = os.parameters.cpu_max;

    if (os.parameters.cpu_default !== undefined) {
      // Adjust the range to show default as the minimum display value
      const offset = os.parameters.cpu_default - os.parameters.cpu_min;
      displayMin = os.parameters.cpu_default;
      displayMax = os.parameters.cpu_max + offset;
    }

    sliderInstances['cpu'].setMinMax(displayMin, displayMax);

    // Set step if defined in OS parameters
    if (os.parameters.cpu_step !== undefined) {
      sliderInstances['cpu'].setStep(os.parameters.cpu_step);
    }
  }

  if (sliderInstances['ram']) {
    // Store original parameters for API conversion (in MB)
    sliderInstances['ram'].originalParams = {
      min: os.parameters.ram_min / 1024, // Convert to GB for consistency
      max: os.parameters.ram_max / 1024,
      step: os.parameters.ram_step ? os.parameters.ram_step / 1024 : undefined,
      default: os.parameters.ram_default ? os.parameters.ram_default / 1024 : undefined,
    };

    // For Windows VPS, disable offset subtraction
    sliderInstances['ram'].noOffsetSubtraction = isWindowsVps;

    // Convert to GB and adjust range if default exists
    let displayMin = os.parameters.ram_min / 1024;
    let displayMax = os.parameters.ram_max / 1024;

    if (os.parameters.ram_default !== undefined) {
      const defaultGB = os.parameters.ram_default / 1024;
      const offset = defaultGB - displayMin;
      displayMin = defaultGB;
      displayMax = displayMax + offset;
    }

    sliderInstances['ram'].setMinMax(displayMin, displayMax);

    // Set step if defined in OS parameters (convert to GB)
    if (os.parameters.ram_step !== undefined) {
      sliderInstances['ram'].setStep(os.parameters.ram_step / 1024);
    }
  }

  if (sliderInstances['capacity']) {
    // Store original parameters for API conversion (in GB)
    sliderInstances['capacity'].originalParams = {
      min: os.parameters.capacity_min / 1024, // Convert to GB for consistency
      max: os.parameters.capacity_max / 1024,
      step: os.parameters.capacity_step ? os.parameters.capacity_step / 1024 : undefined,
      default: os.parameters.capacity_default ? os.parameters.capacity_default / 1024 : undefined,
    };

    // For Windows VPS, disable offset subtraction
    sliderInstances['capacity'].noOffsetSubtraction = isWindowsVps;

    // Convert to GB and adjust range if default exists
    let displayMin = os.parameters.capacity_min / 1024;
    let displayMax = os.parameters.capacity_max / 1024;

    if (os.parameters.capacity_default !== undefined) {
      const defaultGB = os.parameters.capacity_default / 1024;
      const offset = defaultGB - displayMin;
      displayMin = defaultGB;
      displayMax = displayMax + offset;
    }

    sliderInstances['capacity'].setMinMax(displayMin, displayMax);

    // Set step if defined in OS parameters (convert to GB)
    if (os.parameters.capacity_step !== undefined) {
      sliderInstances['capacity'].setStep(os.parameters.capacity_step / 1024);
    }
  }
}
